import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';

const PERMIT_TYPES = [
  {
    type: 'Standard 30-Day Permit',
    icon: 'ri-time-line',
    desc: 'The most common temporary permit — valid for 30 days while you complete full registration.',
    docs: ['Valid driver\'s license', 'Proof of vehicle ownership', 'Proof of insurance (if available)', 'Payment for fees'],
    badge: 'Most Common',
    badgeColor: 'bg-[#003366] text-white',
  },
  {
    type: 'Trip Permit',
    icon: 'ri-roadster-line',
    desc: 'Short-term 1–3 day permit for specific interstate or intrastate trips.',
    docs: ['Valid driver\'s license', 'Vehicle VIN and info', 'Trip destination details', 'Payment for fees'],
    badge: 'Short-Term',
    badgeColor: 'bg-green-600 text-white',
  },
  {
    type: 'Dealer Permit',
    icon: 'ri-store-line',
    desc: 'For licensed vehicle dealers — allows transport and demonstration of vehicles.',
    docs: ['Dealer license', 'Vehicle information', 'Business documentation', 'Payment for fees'],
    badge: 'Business',
    badgeColor: 'bg-slate-600 text-white',
  },
  {
    type: 'Emergency Permit',
    icon: 'ri-alarm-warning-line',
    desc: 'For urgent transportation needs — medical, work, or family emergencies.',
    docs: ['Valid driver\'s license', 'Documentation of emergency', 'Vehicle information', 'Payment for fees'],
    badge: 'Urgent',
    badgeColor: 'bg-red-600 text-white',
  },
];

const SITUATIONS = [
  { icon: 'ri-car-line', title: 'New Vehicle Purchase', desc: 'Just bought a vehicle and need to drive it home while processing full registration.' },
  { icon: 'ri-truck-line', title: 'Out-of-State Move', desc: 'Moving to Nevada with an out-of-state vehicle — permit covers you during the transition.' },
  { icon: 'ri-calendar-line', title: 'Expired Registration', desc: 'Registration has lapsed — temporary permit keeps you legal while renewing.' },
  { icon: 'ri-shield-line', title: 'Insurance Lapse', desc: 'Need time to secure proper Nevada insurance before full registration.' },
  { icon: 'ri-file-line', title: 'Missing Documents', desc: 'Waiting on paperwork — permit covers you while gathering required documents.' },
  { icon: 'ri-alarm-line', title: 'Emergency Travel', desc: 'Urgent need to drive — emergency permit issued immediately.' },
];

const STEPS = [
  { step: '01', icon: 'ri-search-line', title: 'Determine Need', desc: 'Identify the type of permit required for your situation. Call us if unsure.' },
  { step: '02', icon: 'ri-file-edit-line', title: 'Complete Application', desc: 'Fill out the permit application with vehicle details and reason for permit.' },
  { step: '03', icon: 'ri-money-dollar-circle-line', title: 'Pay Fees', desc: 'Process payment for the permit. We accept all major payment methods.' },
  { step: '04', icon: 'ri-award-line', title: 'Receive Permit', desc: 'Permit issued immediately. Instructions and validity dates provided.' },
];

export default function TemporaryPermitsPage() {
  const [openType, setOpenType] = useState<number | null>(0);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Temporary Vehicle Permits Nevada Las Vegas - 30-Day Movement Permits',
    description: 'Immediate temporary vehicle permits in Las Vegas Nevada. 30-day and 60-day movement permits, trip permits, and emergency permits processed same day. Licensed DMV provider. Call 702.979.7202.',
    keywords: 'temporary vehicle permits Nevada, 30-day permit Las Vegas, movement permit Nevada, temporary registration Las Vegas, emergency vehicle permit Nevada',
    canonical: `${siteUrl}/temporary-permits`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema('Temporary Vehicle Permits', 'Immediate temporary vehicle permits including 30-day and 60-day movement permits, trip permits, and emergency permits in Las Vegas Nevada.'),
        generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Services', url: '/services' }, { name: 'Temporary Permits', url: '/temporary-permits' }])
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* HERO */}
      <section className="relative pt-[104px]">
        <div className="relative h-[400px] md:h-[460px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/3f663cb3f5ff0f57868e0aad10bd9680.jpg"
            alt="Temporary Vehicle Permits"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/80"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="flex items-center space-x-2 text-white/60 text-xs mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#F5A623]">Temporary Permits</span>
            </div>
            <div className="inline-flex items-center space-x-2 bg-[#F5A623] text-white text-xs font-bold px-4 py-1.5 rounded mb-4 uppercase tracking-widest">
              <i className="ri-shield-check-line"></i>
              <span>Licensed 3rd Party Nevada DMV Provider</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-3xl">
              Temporary Permits <span className="text-[#F5A623]">Nevada</span>
            </h1>
            <p className="text-white/85 text-lg max-w-2xl mb-7">
              Immediate temporary driving permits for all urgent transportation needs. Issued same day — get back on the road legally.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="fas fa-phone text-xs"></i>
                <span>Call Now: 702.979.7202</span>
              </a>
              <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="ri-calendar-check-line"></i>
                <span>Book Appointment</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NOTICE BAR */}
      <div className="bg-[#FFF8E7] border-b-2 border-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0"></i>
            <p className="text-sm text-[#7a5c00]">
              <strong>We are NOT the Nevada DMV.</strong> Speedy DMV Services is a licensed, independent 3rd party provider. We assist with DMV transactions for a service fee. Official DMV: <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold">dmv.nv.gov</a>
            </p>
          </div>
        </div>
      </div>

      {/* QUICK STATS */}
      <div className="bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1a4480]">
            {[
              { value: '$120–$150 + DMV', label: 'Service Fee' },
              { value: 'Immediate', label: 'Issuance Speed' },
              { value: '30 Days', label: 'Standard Validity' },
              { value: '4 Types', label: 'Permit Options' },
            ].map((s, i) => (
              <div key={i} className="py-4 px-6 text-center">
                <p className="text-[#F5A623] text-lg font-bold">{s.value}</p>
                <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">How to Get Your Permit</h2>
          <p className="text-gray-500 mb-10 pl-5">Four simple steps — permits issued same day in most cases.</p>
          <div className="grid md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-[#003366]/15"></div>
            {STEPS.map((item, i) => (
              <div key={i} className="text-center relative group">
                <div className="w-20 h-20 mx-auto flex items-center justify-center bg-[#003366] rounded-full mb-4 relative z-10 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${item.icon} text-[#F5A623] group-hover:text-white text-2xl transition-colors`}></i>
                </div>
                <span className="text-[#F5A623] text-xs font-bold tracking-widest uppercase">Step {item.step}</span>
                <h3 className="font-bold text-[#003366] text-base mt-1 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERMIT TYPES ACCORDION */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Types of Temporary Permits</h2>
          <p className="text-gray-500 mb-8 pl-5">Select your permit type to see required documents and details.</p>
          <div className="grid md:grid-cols-2 gap-5">
            {PERMIT_TYPES.map((t, i) => (
              <div
                key={i}
                className={`border rounded-lg overflow-hidden transition-all cursor-pointer ${openType === i ? 'border-[#003366] shadow-md' : 'border-gray-200 hover:border-[#003366]/40'}`}
                onClick={() => setOpenType(openType === i ? null : i)}
              >
                <div className={`flex items-center justify-between px-5 py-4 ${openType === i ? 'bg-[#003366]' : 'bg-white hover:bg-gray-50'} transition-colors`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded flex-shrink-0 ${openType === i ? 'bg-[#F5A623]' : 'bg-[#003366]'}`}>
                      <i className={`${t.icon} ${openType === i ? 'text-white' : 'text-[#F5A623]'} text-base`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className={`font-bold text-sm ${openType === i ? 'text-white' : 'text-[#003366]'}`}>{t.type}</p>
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${t.badgeColor}`}>{t.badge}</span>
                      </div>
                      <p className={`text-xs ${openType === i ? 'text-white/70' : 'text-gray-400'}`}>{t.desc}</p>
                    </div>
                  </div>
                  <i className={`${openType === i ? 'ri-arrow-up-s-line text-white' : 'ri-arrow-down-s-line text-[#003366]'} text-xl flex-shrink-0`}></i>
                </div>
                {openType === i && (
                  <div className="px-5 py-4 bg-white border-t border-gray-100">
                    <p className="text-xs font-bold text-[#003366] uppercase tracking-widest mb-3">Required Documents</p>
                    <ul className="space-y-2">
                      {t.docs.map((doc, j) => (
                        <li key={j} className="flex items-center space-x-2 text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0"></i>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <a href="tel:7029797202" className="inline-flex items-center space-x-2 bg-[#003366] hover:bg-[#002244] text-white px-5 py-2.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
                        <i className="fas fa-phone text-xs"></i>
                        <span>Call to Confirm Documents</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMON SITUATIONS */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Common Situations We Handle</h2>
          <p className="text-gray-500 mb-10 pl-5">Whatever your situation, we have a permit solution ready.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SITUATIONS.map((s, i) => (
              <div key={i} className="group flex items-start space-x-4 p-5 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50/30 transition-all">
                <div className="w-11 h-11 flex items-center justify-center bg-[#003366] rounded flex-shrink-0 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${s.icon} text-[#F5A623] group-hover:text-white text-base transition-colors`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#003366] text-sm mb-1">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEGAL DISCLAIMER */}
      <section className="py-8 bg-[#FFF8E7] border-t border-[#F5A623]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0 mt-0.5"></i>
            <p className="text-sm text-[#7a5c00] font-medium leading-relaxed">
              I AM NOT AN ATTORNEY IN THE STATE OF NEVADA. I AM NOT AUTHORIZED TO GIVE LEGAL ADVICE OR LEGAL REPRESENTATION. I MAY NOT ACCEPT FEES FOR GIVING LEGAL ADVICE OR LEGAL REPRESENTATION.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Need a Temporary Permit <span className="text-[#F5A623]">Now?</span>
          </h2>
          <p className="text-white/75 mb-8 leading-relaxed">
            Don't wait — get your temporary driving permit immediately and get back on the road legally.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="fas fa-phone text-sm"></i>
              <span>Call 702.979.7202</span>
            </a>
            <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="ri-calendar-check-line"></i>
              <span>Book Appointment</span>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-5">
            {['Licensed 3rd Party Provider', 'Immediate Issuance', 'All Permit Types', 'Same-Day Service'].map(b => (
              <div key={b} className="flex items-center space-x-1.5 text-white/60 text-xs">
                <i className="ri-checkbox-circle-fill text-[#F5A623]"></i>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
